import { useContext, useEffect, useMemo, useState } from 'react';
import { addsToVault, vaultAccess } from 'helpers/sharing';
import { ChevronIcon } from 'assets/icons';
import { NotificationType, VaultNavigationTabs } from 'common/enums';
import { ButtonStyle } from 'common/button';
import { ModalWarning } from 'common/modal-warning';
import { VaultDetailContext } from 'context/vault-detail-context';
import { vaultDetailActions } from 'context/vault-detail-context/action-types';
import { AppContext, appActions } from 'context/app-context';
import { albumDisplayName, excludeDefaultAlbum } from 'helpers/utils';
import { Album } from 'models/album';
import { NotificationObject } from 'models/notificationObject';
import { VaultsController } from 'networking/controllers/vaults-controller';
import { AlbumContentSerialized } from 'networking/types/album-detail';
import { SharedAlbum } from 'networking/types/shared-album';
import { useCreateAlbum } from 'pages/vault-detail/hooks/use-create-album';
import { NewAlbumModal } from 'pages/vault-detail/components/vault-top-bar/components/new-album-modal';
import { goToPage, RouteName } from 'routes';
import { useIncrementalList } from 'hooks/use-incremental-list';
import { useHorizontalScroll } from 'hooks/use-horizontal-scroll';
import { isPromptContent, memoriesOf } from 'helpers/memories';
import { withOrigin } from 'helpers/vault-breadcrumbs';
import { applyPendingOps } from '../../move-memories/apply-pipeline';
import { opId } from '../../move-memories/types';
import { EmptyDashboardState } from '../albums/components/empty-dashboard-state';
import { filterAlbumsBySearch, sortAlbums } from '../utils/album-list-utils';
import { MemoryItem, sortMemories } from '../utils/memory-list-utils';
import { CollectionCard } from './components/collection-card';
import { NewAlbumCard } from './components/new-album-card';
import { MemoryCard } from './components/memory-card';
import { PersonFilterRow, FilterPerson } from './components/person-filter-row';
import { DashboardPrompt, PromptsSection } from './components/prompts-section';
import {
  MemoriesFilter,
  MemoriesFilterValue,
} from './components/memories-filter';
import { SortDropdown } from '../../sort-dropdown';
import styles from './dashboard.module.scss';

// Drag payload while a memory is dragged from the grid onto an album card.
// An unorganized memory (from the hidden default album) is moved into the
// album it's dropped on; a memory already in an album is copied.
type DragMemory = { contentId: number; sourceAlbumId: number; move: boolean };

// The album a memory was dropped on, awaiting confirmation.
type DropTarget = {
  contentId: number;
  sourceAlbumId: number;
  album: Album;
  move: boolean;
};

const DRAG_MIME = 'application/x-gs-memory';

// What the memories section says when it has none to show.
const emptyMemoriesMessage = (query: string, showUnorganized: boolean) => {
  if (query) return 'No memories match your search.';
  if (showUnorganized) {
    return 'Nice Job. All your memories are organized into the albums';
  }
  return 'No memories yet.';
};

export const Dashboard = () => {
  const {
    state: {
      vaultId,
      answeredAlbums,
      unansweredAlbums,
      albumsSortBy,
      searchQuery,
      scope,
    },
    dispatch: vaultDispatch,
  } = useContext(VaultDetailContext);
  const { state: appState, dispatch: appDispatch } = useContext(AppContext);
  // The vault's custom order, for AlbumsSortBy.custom.
  const memoryOrder = appState.currentVault?.memoryOrder;
  const createAlbum = useCreateAlbum();

  // "New Album" card → same flow as the top-bar "New +" menu: name it in a
  // modal, create it, then open its page.
  const [showNewAlbumModal, setShowNewAlbumModal] = useState(false);

  // In their own vault "Shared With Me" is the albums others shared with them
  // from other vaults. In someone else's vault they add to, My Stuff lists
  // everything in it they may see and Shared With Me what others added; one
  // they only see has Shared With Me alone, with everything.
  const myVaultAccess = vaultAccess(appState.data.user, appState.currentVault);
  const inOthersVault =
    !!appState.currentVault && myVaultAccess.role !== 'owner';
  const isShared = scope === 'shared' && !inOthersVault;
  const othersOnly =
    inOthersVault && scope === 'shared' && addsToVault(myVaultAccess);

  // Breadcrumb origin for screens opened from here: this vault's dashboard,
  // with the scope so a shared album (which lives in another vault) can lead
  // back to "Shared With Me".
  const origin = withOrigin({
    vaultId,
    tab: VaultNavigationTabs.dashboard,
    scope,
  });

  const handleCreateNamedAlbum = async (name: string) => {
    setShowNewAlbumModal(false);
    const album = await createAlbum(vaultId, name);
    if (!album) return;
    goToPage(
      RouteName.AlbumScreen,
      { id: vaultId, albumId: album.id },
      {},
      origin,
    );
  };
  const [sharedAlbums, setSharedAlbums] = useState<SharedAlbum[]>([]);
  const [sharedLoading, setSharedLoading] = useState(false);
  const [personFilter, setPersonFilter] = useState<number | null>(null);

  // Drag-and-drop copy of a memory onto an album that doesn't have it yet.
  const [dragging, setDragging] = useState<DragMemory | null>(null);
  const [hoverAlbumId, setHoverAlbumId] = useState<number | null>(null);
  const [dropTarget, setDropTarget] = useState<DropTarget | null>(null);
  const [dropping, setDropping] = useState(false);

  // Load albums shared with me whenever the "Shared With Me" scope is active.
  useEffect(() => {
    if (!isShared) {
      setPersonFilter(null);
      return undefined;
    }
    let cancelled = false;
    setSharedLoading(true);
    VaultsController.getSharedWithMe()
      .then((albums) => {
        if (!cancelled) setSharedAlbums(albums);
      })
      .catch(() => {
        if (!cancelled) setSharedAlbums([]);
      })
      .finally(() => {
        if (!cancelled) setSharedLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isShared]);

  // The unique people who have shared albums with me (drives the filter row).
  const people = useMemo<FilterPerson[]>(() => {
    const map = new Map<number, FilterPerson>();
    sharedAlbums.forEach((album) => {
      const sharer = album.sharedBy;
      if (sharer?.id != null && !map.has(sharer.id)) {
        map.set(sharer.id, {
          id: sharer.id,
          name: sharer.name,
          email: sharer.email,
        });
      }
    });
    return [...map.values()];
  }, [sharedAlbums]);

  // Both lists: the context files albums with no entries under
  // `unansweredAlbums`, and a just-created album is always empty — so reading
  // only `answeredAlbums` meant a new album could never appear here.
  // Includes the hidden default album: its contents are the memories the user
  // never filed anywhere, and the Memories section below still shows them.
  const ownAlbums: Album[] = useMemo(
    () =>
      [...answeredAlbums, ...unansweredAlbums].sort(
        (a, b) =>
          new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
      ),
    [answeredAlbums, unansweredAlbums],
  );

  const baseAlbums: Album[] = useMemo(() => {
    if (!isShared) return ownAlbums;
    if (personFilter == null) return sharedAlbums;
    return sharedAlbums.filter((album) => album.sharedBy?.id === personFilter);
  }, [isShared, ownAlbums, sharedAlbums, personFilter]);

  // The albums row, unlike the memories list, must not surface the default one.
  const albums = useMemo(
    () =>
      sortAlbums(
        filterAlbumsBySearch(
          excludeDefaultAlbum(baseAlbums).filter(
            (album) => !othersOnly || !album.myAccess?.mine,
          ),
          searchQuery,
        ),
        albumsSortBy,
      ),
    [baseAlbums, searchQuery, albumsSortBy, othersOnly],
  );

  const allMemories = useMemo(
    (): MemoryItem[] =>
      baseAlbums.flatMap((album) =>
        memoriesOf(album)
          .filter((content) => !othersOnly || !content.myAccess?.mine)
          .map((content) => ({
            ...content,
            albumId: album.id,
            sharedBy: isShared ? (album as SharedAlbum).sharedBy : undefined,
            unsorted: !!album.isDefault,
          })),
      ),
    [baseAlbums, isShared, othersOnly],
  );

  // The vault's unanswered interview questions (My Stuff only, not while
  // searching), newest album first like the albums row.
  const prompts = useMemo(
    (): DashboardPrompt[] =>
      ownAlbums.flatMap((album) =>
        (album.contents ?? []).filter(isPromptContent).map((content) => ({
          content,
          albumId: album.id,
          albumName: albumDisplayName(album),
          inDefaultAlbum: !!album.isDefault,
        })),
      ),
    [ownAlbums],
  );

  // Without a search, My Stuff lists either all memories (the default) or
  // only the ones that aren't in an album yet ("Without Album"); a search
  // looks through every memory.
  const [memoryFilter, setMemoryFilter] = useState<MemoriesFilterValue>('all');
  const query = searchQuery.trim().toLowerCase();
  const showFilter = !isShared && !query;
  const showUnorganized = showFilter && memoryFilter === 'unsorted';
  const memoryCounts = useMemo(
    () => ({
      unsorted: allMemories.filter((memory) => memory.unsorted).length,
      all: allMemories.length,
    }),
    [allMemories],
  );

  const memories = useMemo(() => {
    let list = allMemories;
    if (query) {
      list = list.filter((memory) =>
        memory.name?.toLowerCase().includes(query),
      );
    } else if (showUnorganized) {
      list = list.filter((memory) => memory.unsorted);
    }
    return sortMemories(list, albumsSortBy, memoryOrder);
  }, [allMemories, query, showUnorganized, albumsSortBy, memoryOrder]);

  const openAlbum = (album: Album) => {
    const targetVault = isShared
      ? ((album as SharedAlbum).vaultId ?? vaultId)
      : vaultId;
    goToPage(
      RouteName.AlbumScreen,
      { id: targetVault, albumId: album.id },
      {},
      origin,
    );
  };

  // Opening a memory goes to the vault-wide "Memories" view, focused on it.
  const openMemory = (contentId: number, albumId: number) => {
    const targetVault = isShared
      ? (sharedAlbums.find((album) => album.id === albumId)?.vaultId ?? vaultId)
      : vaultId;
    goToPage(
      RouteName.MemoriesViewScreen,
      { id: targetVault },
      { content: contentId },
      origin,
    );
  };

  // Dragging (to copy or move into another album) is only meaningful for the user's
  // own vault — shared albums aren't theirs to add entries to — and only for
  // those who may move anything in it (owner and editors).
  const myAccess = myVaultAccess;
  const canDragMemories = !isShared && myAccess.fullAccess;
  // Answering prompts adds memories; viewers and limited contributors can't.
  const canAnswerPrompts =
    myAccess.fullAccess || myAccess.role === 'contributor';

  // Reveal the grid in chunks as the user scrolls so a big vault doesn't
  // request every thumbnail at once.
  const {
    visibleItems: visibleMemories,
    hasMore: hasMoreMemories,
    sentinelRef: memoriesSentinelRef,
  } = useIncrementalList(memories);

  // Left/right arrows at the top right of the albums row, shown once the
  // albums no longer fit.
  const albumsScroll = useHorizontalScroll<HTMLDivElement>();
  const albumsOverflow =
    albumsScroll.canScrollLeft || albumsScroll.canScrollRight;

  // Lookup used by the reused copy pipeline to find the source content.
  const contentsById = useMemo(() => {
    const map = new Map<number, AlbumContentSerialized>();
    memories.forEach((memory) => map.set(memory.id, memory));
    return map;
  }, [memories]);

  const notify = (title: string, type: NotificationType) =>
    appDispatch({
      type: appActions.NOTIFICATION,
      notification: new NotificationObject({ show: true, title, type }),
    });

  const handleMemoryDragStart =
    (contentId: number, sourceAlbumId: number, move: boolean) =>
    (e: React.DragEvent<HTMLButtonElement>) => {
      const payload: DragMemory = { contentId, sourceAlbumId, move };
      e.dataTransfer.setData(DRAG_MIME, JSON.stringify(payload));
      e.dataTransfer.effectAllowed = move ? 'move' : 'copy';
      setDragging(payload);
    };

  const handleMemoryDragEnd = () => {
    setDragging(null);
    setHoverAlbumId(null);
  };

  // A memory can only be dropped on an album that doesn't already have it.
  // A memory belongs to its source album, so every other album is a target.
  const isAlbumTarget = (album: Album) =>
    !!dragging && dragging.sourceAlbumId !== album.id;

  const handleAlbumDragOver =
    (album: Album) => (e: React.DragEvent<HTMLButtonElement>) => {
      if (!isAlbumTarget(album)) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = dragging?.move ? 'move' : 'copy';
      setHoverAlbumId(album.id);
    };

  const handleAlbumDragLeave = (album: Album) => () => {
    setHoverAlbumId((prev) => (prev === album.id ? null : prev));
  };

  const handleAlbumDrop =
    (album: Album) => (e: React.DragEvent<HTMLButtonElement>) => {
      e.preventDefault();
      setHoverAlbumId(null);
      if (!dragging || dragging.sourceAlbumId === album.id) return;
      setDropTarget({
        contentId: dragging.contentId,
        sourceAlbumId: dragging.sourceAlbumId,
        album,
        move: dragging.move,
      });
      setDragging(null);
    };

  const closeDropModal = () => {
    if (dropping) return;
    setDropTarget(null);
  };

  // Patches an album in context whichever list holds it (an album without
  // memories sits in the unanswered one).
  const patchAlbum = (albumId: number, updates: Partial<Album>) => {
    vaultDispatch({
      type: vaultDetailActions.UPDATE_ANSWERED_ALBUM,
      albumId,
      updates,
    });
    vaultDispatch({
      type: vaultDetailActions.UPDATE_UNANSWERED_ALBUM,
      albumId,
      updates,
    });
  };

  // Moving keeps the memory itself (its date, file and id) and only changes
  // its album. Copying reuses the copy pipeline of the reorganize screen.
  const moveOrCopy = async ({
    contentId,
    sourceAlbumId,
    album,
    move,
  }: DropTarget) => {
    if (move) {
      await VaultsController.updateContentMetadata(contentId, {
        albumId: album.id,
      });
      return;
    }
    const result = await applyPendingOps({
      vaultId,
      contentsById,
      ops: [
        {
          id: opId(contentId, album.id),
          contentId,
          sourceAlbumId,
          targetAlbumId: album.id,
          action: 'copy',
        },
      ],
    });
    if (result.ok === 0) throw new Error('copy failed');
  };

  const handleConfirmDrop = async () => {
    if (!dropTarget || dropping) return;
    const { contentId, sourceAlbumId, album, move } = dropTarget;
    setDropping(true);
    try {
      await moveOrCopy(dropTarget);
    } catch (error) {
      setDropping(false);
      setDropTarget(null);
      notify(
        move
          ? 'The memory could not be moved'
          : 'The memory could not be copied',
        NotificationType.Error,
      );
      return;
    }

    // A moved memory leaves its old album (Unorganized Memories) at once.
    if (move) {
      const source = ownAlbums.find((a) => a.id === sourceAlbumId);
      if (source) {
        patchAlbum(sourceAlbumId, {
          contents: (source.contents ?? []).filter((c) => c.id !== contentId),
        });
      }
    }

    // Refresh only the target album and patch it into context, so the
    // memory appears in place without reloading/remounting the whole page.
    try {
      const fresh = await VaultsController.getAlbum(album.id);
      patchAlbum(album.id, {
        contents: fresh.contents,
        thumbnailUrl: fresh.thumbnailUrl,
        totalSize: fresh.totalSize,
      });
    } catch (error) {
      // The change succeeded server-side; fall back to a full refetch only
      // if the targeted refresh failed, so the view still reconciles.
      appDispatch({ type: appActions.SET_REFETCH_VAULT, refetchVault: true });
    }
    setDropping(false);
    setDropTarget(null);
    notify(
      `Memory ${move ? 'moved' : 'copied'} to ${album.name}`,
      NotificationType.Success,
    );
  };

  // Empty / loading states.
  // Only truly empty vaults get the empty state — a vault holding nothing but
  // entry-less albums still has albums to show. Every vault owns a hidden
  // default album, so emptiness is measured on the visible albums plus any
  // unfiled memories rather than on `ownAlbums` itself.
  // The albums row: hidden when it would be empty (no albums of theirs here
  // and nothing to create, e.g. a memory added to someone else's album).
  // New albums belong in My Stuff.
  const showNewAlbumCard = !isShared && !othersOnly && myAccess.canCreateAlbum;
  const showAlbumsRow = albums.length > 0 || showNewAlbumCard;

  // Limited contributors see only what they add, so their vault starts out
  // empty: say why, and what they can do.
  const limitedNote = !isShared && myAccess.role === 'limited_contributor' && (
    <p className={styles.limitedNote}>
      You can add memories to this vault with <strong>New</strong>. You see only
      the memories you add.
    </p>
  );
  // (An empty My Stuff still offers New Album, below, to those who may add
  // one.)
  if (
    inOthersVault &&
    !showNewAlbumCard &&
    albums.length === 0 &&
    allMemories.length === 0 &&
    !searchQuery.trim()
  ) {
    return (
      limitedNote || (
        <p className={styles.empty}>
          {othersOnly
            ? 'No one else has added anything to this vault yet.'
            : 'There’s nothing in this vault yet.'}
        </p>
      )
    );
  }
  if (
    !isShared &&
    excludeDefaultAlbum(ownAlbums).length === 0 &&
    allMemories.length === 0
  ) {
    return limitedNote || <EmptyDashboardState />;
  }
  if (isShared && sharedLoading && sharedAlbums.length === 0) {
    return <p className={styles.empty}>Loading shared albums…</p>;
  }
  if (isShared && !sharedLoading && sharedAlbums.length === 0) {
    return (
      <p className={styles.empty}>No albums have been shared with you yet.</p>
    );
  }

  return (
    <div className={styles.dashboard}>
      {limitedNote}
      {isShared && (
        <PersonFilterRow
          people={people}
          selectedId={personFilter}
          onSelect={setPersonFilter}
        />
      )}
      {!isShared &&
        canAnswerPrompts &&
        !searchQuery.trim() &&
        prompts.length > 0 && (
          <PromptsSection
            vaultId={vaultId}
            vault={appState.currentVault}
            prompts={prompts}
            defaultAskedBy={
              appState.currentVault?.owner?.name ?? 'Generational Story'
            }
            answeredIn={(albumId) =>
              memoriesOf(ownAlbums.find((album) => album.id === albumId) ?? {})
            }
          />
        )}
      {showAlbumsRow && (
        <section className={styles.section}>
          <div className={styles.albumsHeader}>
            <span className={styles.sectionTitle}>Albums</span>
            {albumsOverflow && (
              <div className={styles.scrollButtons}>
                <button
                  type="button"
                  className={styles.scrollButton}
                  onClick={() => albumsScroll.scrollPage(-1)}
                  disabled={!albumsScroll.canScrollLeft}
                  aria-label="Scroll albums left"
                >
                  <ChevronIcon className={styles.scrollLeftIcon} />
                </button>
                <button
                  type="button"
                  className={styles.scrollButton}
                  onClick={() => albumsScroll.scrollPage(1)}
                  disabled={!albumsScroll.canScrollRight}
                  aria-label="Scroll albums right"
                >
                  <ChevronIcon className={styles.scrollRightIcon} />
                </button>
              </div>
            )}
          </div>
          <div className={styles.albumsRow}>
            <div className={styles.albumsScroll} ref={albumsScroll.ref}>
              {showNewAlbumCard && (
                <NewAlbumCard onClick={() => setShowNewAlbumModal(true)} />
              )}
              {albums.map((album) => (
                <CollectionCard
                  key={album.id}
                  album={album}
                  onClick={() => openAlbum(album)}
                  sharedBy={
                    isShared ? (album as SharedAlbum).sharedBy : undefined
                  }
                  onDragOver={
                    canDragMemories ? handleAlbumDragOver(album) : undefined
                  }
                  onDragLeave={
                    canDragMemories ? handleAlbumDragLeave(album) : undefined
                  }
                  onDrop={canDragMemories ? handleAlbumDrop(album) : undefined}
                  isDropActive={canDragMemories && isAlbumTarget(album)}
                  isDropTarget={hoverAlbumId === album.id}
                  isDropDisabled={
                    !!dragging && dragging.sourceAlbumId === album.id
                  }
                />
              ))}
            </div>
            {/* Hints at more albums to the right; gone at the end of the row. */}
            {albumsScroll.canScrollRight && <div className={styles.fade} />}
          </div>
        </section>
      )}
      <section className={styles.section}>
        {/* Figma: GS UI / Design System, node 32474:15277 (header). */}
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Memories</span>
          {showFilter && (
            <MemoriesFilter
              value={memoryFilter}
              counts={memoryCounts}
              onChange={setMemoryFilter}
            />
          )}
          <SortDropdown size="small" />
          {!isShared && myAccess.fullAccess && (
            <button
              type="button"
              className={styles.moveMemoriesButton}
              onClick={() => goToPage(RouteName.MoveMemories, { id: vaultId })}
            >
              Move Memories
            </button>
          )}
        </div>
        {memories.length > 0 ? (
          <>
            <div className={styles.grid}>
              {visibleMemories.map((memory) => (
                <MemoryCard
                  key={memory.id}
                  content={memory}
                  onClick={() => openMemory(memory.id, memory.albumId)}
                  sharedBy={
                    memory.sharedBy ? { name: memory.sharedBy.name } : undefined
                  }
                  draggable={canDragMemories && !dropping}
                  onDragStart={
                    canDragMemories
                      ? handleMemoryDragStart(
                          memory.id,
                          memory.albumId,
                          !!memory.unsorted,
                        )
                      : undefined
                  }
                  onDragEnd={canDragMemories ? handleMemoryDragEnd : undefined}
                  isDragging={dragging?.contentId === memory.id}
                />
              ))}
            </div>
            {hasMoreMemories && <div ref={memoriesSentinelRef} aria-hidden />}
          </>
        ) : (
          <p className={styles.empty}>
            {emptyMemoriesMessage(query, showUnorganized)}
          </p>
        )}
      </section>

      {showNewAlbumModal && (
        <NewAlbumModal
          onSubmit={handleCreateNamedAlbum}
          onClose={() => setShowNewAlbumModal(false)}
        />
      )}

      {dropTarget && (
        <ModalWarning
          title={dropTarget.move ? 'Move memory' : 'Copy memory'}
          content={`Are you sure you want to ${
            dropTarget.move ? 'move' : 'copy'
          } this memory to the album ${dropTarget.album.name}?`}
          successButtonText={dropTarget.move ? 'Move' : 'Copy'}
          successStyleButton={ButtonStyle.Primary}
          loading={dropping}
          disableManualClosing={dropping}
          closeFn={closeDropModal}
          cancelActionFn={closeDropModal}
          successFn={handleConfirmDrop}
        />
      )}
    </div>
  );
};
